export const SELF_SIGN_SIGNATURE_COLORS = [
  { id: 'black', value: '#000000', labelKey: 'signatureColorBlack' },
  { id: 'red', value: '#D32F2F', labelKey: 'signatureColorRed' },
  { id: 'blue', value: '#1565C0', labelKey: 'signatureColorBlue' },
  { id: 'green', value: '#2E7D32', labelKey: 'signatureColorGreen' },
] as const;

export const DEFAULT_SELF_SIGN_SIGNATURE_COLOR = SELF_SIGN_SIGNATURE_COLORS[0].value;

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '').trim();
  const full =
    normalized.length === 3
      ? normalized.split('').map((c) => c + c).join('')
      : normalized;
  const num = parseInt(full, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

/** Recolor signature ink while preserving anti-aliased edges. */
export function recolorSignatureImage(dataUrl: string, color: string): Promise<string> {
  const trimmed = (dataUrl ?? '').trim();
  if (!trimmed) return Promise.resolve('');
  if (!color) return Promise.resolve(trimmed);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(trimmed);
        return;
      }

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const { r, g, b } = hexToRgb(color);

      for (let i = 0; i < imageData.data.length; i += 4) {
        const alpha = imageData.data[i + 3];
        if (alpha < 12) continue;

        const lum =
          0.299 * imageData.data[i] +
          0.587 * imageData.data[i + 1] +
          0.114 * imageData.data[i + 2];
        const inkAlpha = Math.round(((255 - lum) / 255) * alpha);
        if (inkAlpha < 10) {
          imageData.data[i + 3] = 0;
          continue;
        }

        imageData.data[i] = r;
        imageData.data[i + 1] = g;
        imageData.data[i + 2] = b;
        imageData.data[i + 3] = inkAlpha;
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to load signature image'));
    img.src = trimmed;
  });
}

export function stripSignatureBackground(dataUrl: string): Promise<string> {
  const trimmed = (dataUrl ?? '').trim();
  if (!trimmed) return Promise.resolve('');

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(trimmed);
        return;
      }

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < imageData.data.length; i += 4) {
        const alpha = imageData.data[i + 3];
        if (alpha < 12) continue;

        const lum =
          0.299 * imageData.data[i] +
          0.587 * imageData.data[i + 1] +
          0.114 * imageData.data[i + 2];
        const inkAlpha = Math.round(((255 - lum) / 255) * alpha);
        if (inkAlpha < 10) {
          imageData.data[i + 3] = 0;
          continue;
        }

        imageData.data[i + 3] = inkAlpha;
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to load signature image'));
    img.src = trimmed;
  });
}

export async function stripSignatureBackgroundFromBase64(
  base64OrDataUrl: string,
): Promise<string> {
  const trimmed = (base64OrDataUrl ?? '').trim();
  if (!trimmed) return '';
  const dataUrl = trimmed.startsWith('data:')
    ? trimmed
    : `data:image/png;base64,${trimmed}`;
  const stripped = await stripSignatureBackground(dataUrl);
  const idx = stripped.indexOf('base64,');
  return idx !== -1 ? stripped.substring(idx + 7) : stripped;
}
